import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { MessageInstanceObjectType, MessageInstanceRuleObjectTypeResponse } from "app/views/campaign-3/models/campaign-3-info-base.model";
import { MessageInstanceObjectTypes, MessageInstanceRule, MessageInstanceRuleDetail, MessageInstanceRuleTypes } from "app/views/campaign-3/models/campaign-3.model";
import { MessageService } from "primeng/primeng";

@Component({
  selector: 'app-touchpoint-rule-popup',
  templateUrl: './touchpoint-rule-popup.component.html',
  styleUrls: ['./touchpoint-rule-popup.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class TouchpointRulePopup implements OnInit {

  form: FormGroup;

  @Input() miId: number;
  @Input() scope: string;
  @Input() rulesData: MessageInstanceRuleObjectTypeResponse[] = [];
  @Input() idTouchpoint: number;
  @Input() miRuleTypesList: Array<MessageInstanceRuleTypes> = [];
  @Input() miObjectTypesList: Array<MessageInstanceObjectTypes> = [];
  @Input() targetScope: string;
  @Input() language: string = "it";
  @Output() rulesCreated = new EventEmitter<MessageInstanceRule[]>();
  
  
  private _ruleToEdit: MessageInstanceRule | null = null;
  
  // Oggetto per tracciare gli errori per ogni campo
  fieldErrors: { [index: number]: { [field: string]: string[] } } = {};

  @Input()
  set ruleToEdit(value: MessageInstanceRule | null) {
    this._ruleToEdit = value;
    if (this.form) {
      if (value) {
        this.populateForm(value);
      } else {
        this.resetForm();
      }
    }
  }
  get ruleToEdit(): MessageInstanceRule | null {
    return this._ruleToEdit;
  }

  constructor(
    private fb: FormBuilder,
    private srvNotify: MessageService,
    public translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.buildForm();
    if (this._ruleToEdit) {
      this.populateForm(this._ruleToEdit);
    } else {
      this.addRuleModule();
    }
  }

  // ================================
  // FORM MANAGEMENT
  // ================================

  private buildForm(): void {
    this.form = this.fb.group({
      rules: this.fb.array([])
    });
  }

  get rules(): FormArray {
    return this.form.get('rules') as FormArray;
  }

  private clearRules(): void {
    while (this.rules.length !== 0) {
      this.rules.removeAt(0);
    }
  }

  addRuleModule(): void {
    const group = this.fb.group({
      ruleTypeId: [null],
      objectTypeId: [null],
      inputValue: ['']
    });
    this.rules.push(group);
  }

  removeRuleModule(index: number): void {
    this.rules.removeAt(index);
    //this.buildRules();
  }

  resetForm(): void {
    this.clearRules();
    this.addRuleModule();
  }

  populateForm(rule: MessageInstanceRule): void {
    if (!this.form) this.buildForm();
    this.clearRules();

    for (let i = 0; i < rule.messageInstanceRuleDetail.length; i++) {
      const d = rule.messageInstanceRuleDetail[i];
      this.rules.push(this.fb.group({
        ruleTypeId: [d.objectId],
        objectTypeId: [d.objectTypeId],
        inputValue: [d.objectValue]
      }));
    }
  }

  // ================================
  // FORM CONTROLS ACCESS HELPERS
  // ================================

  getRuleTypeControl(index: number): FormControl {
    const group = this.rules.at(index) as FormGroup;
    return group.get('ruleTypeId') as FormControl;
  }

  getObjectTypeControl(index: number): FormControl {
    const group = this.rules.at(index) as FormGroup;
    return group.get('objectTypeId') as FormControl;
  }

  getInputValueControl(index: number): FormControl {
    const group = this.rules.at(index) as FormGroup;
    return group.get('inputValue') as FormControl;
  }

  // ================================
  // EVENT HANDLERS (SEMPLIFICATI)
  // ================================

  onRuleTypeChange(index: number): void {
    // ✅ Solo reset dell'object type, nessun controllo
    const group = this.rules.at(index) as FormGroup;
    if (group) {
      const objectTypeControl = group.get('objectTypeId');
      if (objectTypeControl) {
        objectTypeControl.setValue(null);
      }
    }

    this.fieldErrors = {};
  }

  onObjectTypeChange(index: number): void {
    // ✅ Solo impostazione del valore di default, nessun controllo
    const group = this.rules.at(index) as FormGroup;
    if (group) {
      const inputControl = group.get('inputValue');
      if (inputControl.value == null || inputControl.value == undefined || inputControl.value == "") {
        inputControl.setValue(this.getDefault(index));
      }
    }
   this.fieldErrors = {};

  }

  // ================================
  // GETTERS FOR TEMPLATE (SEMPLIFICATI)
  // ================================

  getInputType(index: number): string {
    let res = "text";
    let ruleData = this.getRuleDataByIndexControl(index);
    if (ruleData && ruleData.uiType === 'EDIT' && ruleData.fieldType === "Numeric") {
      res = "number";
    }
    return res;
  }

  getMin(index: number): number | null {
    let res = null;
    let ruleData = this.getRuleDataByIndexControl(index);
    if (ruleData && ruleData.uiType === 'EDIT' && ruleData.fieldType === "Numeric") {
      let objectType = this.getObjectTypeByIndexControl(index);
      if (objectType) {
        res = (objectType.valMin !== undefined && objectType.valMin !== null) ? objectType.valMin : 1;
      }
    }
    return res;
  }

  getMax(index: number): number | null {
    let res = null;
    let ruleData = this.getRuleDataByIndexControl(index);
    if (ruleData && ruleData.uiType === 'EDIT' && ruleData.fieldType === "Numeric") {
      let objectType = this.getObjectTypeByIndexControl(index);
      if (objectType) {
        res = (objectType.valMax !== undefined && objectType.valMax !== null) ? objectType.valMax : null;
      }
    }
    return res;
  }

  getDefault(index: number): string | number | null {
    let res = null;
    let ruleData = this.getRuleDataByIndexControl(index);
    let objectType = this.getObjectTypeByIndexControl(index);
    if (ruleData && ruleData.uiType === 'EDIT' && ruleData.fieldType === "Numeric") {
      if (objectType) {
        res = (objectType.defaultValue !== undefined && objectType.defaultValue !== null) ? +objectType.defaultValue : null;
      }
    }
    return res;
  }

  // ================================
  // BUSINESS LOGIC HELPERS
  // ================================

  private getRuleDataByType(ruleTypeId: number): MessageInstanceRuleObjectTypeResponse | null {
    return this.rulesData.find(rd => rd.messageInstanceRuleTypeId === ruleTypeId) || null;
  }

  getRuleDataByIndexControl(index: number): MessageInstanceRuleObjectTypeResponse | null {
    const group = this.rules.at(index) as FormGroup;
    if (!group) return null;
    
    const rtCtrl = group.get('ruleTypeId');
    if (!rtCtrl) return null;
    
    const ruleTypeId = rtCtrl.value;
    if (!ruleTypeId || ruleTypeId <= 0) return null;
    
    return this.getRuleDataByType(ruleTypeId);
  }

  getObjectTypeByIndexControl(index: number): MessageInstanceObjectType | null {
    let res = null;
    let ruleData = this.getRuleDataByIndexControl(index);
    const group = this.rules.at(index) as FormGroup;
    const objectTypeControl = group.get('objectTypeId');
    
    if (objectTypeControl && ruleData) {
      let objectValue = objectTypeControl.value;
      if (objectValue > 0) {
        let objectType = ruleData.messageInstanceObjectTypeIdList.find(x => x.messageInstanceObjectTypeId == objectValue);
        if (objectType) {
          res = objectType;
        }
      }
    }
    return res;
  }

  private getSelectedCountForType(ruleTypeId: number): number {
    let count = 0;
    for (let i = 0; i < this.rules.length; i++) {
      const group = this.rules.at(i) as FormGroup;
      const rt = group.get('ruleTypeId') ? group.get('ruleTypeId').value : null;
      const ot = group.get('objectTypeId') ? group.get('objectTypeId').value : null;
      if (rt === ruleTypeId && ot != null) count++;
    }
    return count;
  }

  private getSelectedObjectTypeIdsForType(ruleTypeId: number, excludeIndex: number): number[] {
    const selected: number[] = [];
    for (let i = 0; i < this.rules.length; i++) {
      if (i === excludeIndex) continue;

      const group = this.rules.at(i) as FormGroup;
      const rtCtrl = group.get('ruleTypeId');
      const otCtrl = group.get('objectTypeId');

      const rtVal = rtCtrl ? rtCtrl.value : null;
      const otVal = otCtrl ? otCtrl.value : null;

      if (rtVal === ruleTypeId && otVal != null) {
        selected.push(otVal);
      }
    }
    return selected;
  }

  // ================================
  // UI STATE HELPERS (SEMPLIFICATI)
  // ================================

  getAvailableObjectTypesAt(index: number): MessageInstanceObjectType[] {
    const group = this.rules.at(index) as FormGroup;
    if (!group) return [];

    const rtCtrl = group.get('ruleTypeId');
    const otCtrl = group.get('objectTypeId');

    const ruleTypeId = rtCtrl ? rtCtrl.value : null;
    const currentSelected = otCtrl ? otCtrl.value : null;

    if (ruleTypeId === null) return [];

    const ruleData = this.getRuleDataByType(ruleTypeId);
    if (!ruleData) return [];

    const selectedInOthers = this.getSelectedObjectTypeIdsForType(ruleTypeId, index);

    const available: MessageInstanceObjectType[] = [];
    for (let i = 0; i < ruleData.messageInstanceObjectTypeIdList.length; i++) {
      const obj = ruleData.messageInstanceObjectTypeIdList[i];
      const usedSomewhereElse = selectedInOthers.indexOf(obj.messageInstanceObjectTypeId) !== -1;
      const isCurrentSelection = obj.messageInstanceObjectTypeId === currentSelected;

      if (!usedSomewhereElse || isCurrentSelection) {
        available.push(obj);
      }
    }

    available.sort((a, b) => (a.description || '').localeCompare(b.description || ''));
    return available;
  }

  isObjectTypeDisabled(index: number): boolean {
    const group = this.rules.at(index) as FormGroup;
    if (!group) return true;

    const rtCtrl = group.get('ruleTypeId');
    const ruleTypeId = rtCtrl ? rtCtrl.value : null;

    return ruleTypeId === null;
  }

  isInputDisabled(index: number): boolean {
    const ruleData = this.getRuleDataByIndexControl(index);
    let res = ruleData ? ruleData.uiType === 'DROPDOWN' : false;
    if (res == true) {
      const group = this.rules.at(index) as FormGroup;
      group.get('inputValue').setValue('');
    }
    return res;
  }

  getSelectionProgressLabel(index: number): string {
    const group = this.rules.at(index) as FormGroup;
    if (!group) return '';

    const rtCtrl = group.get('ruleTypeId');
    const ruleTypeId = rtCtrl ? rtCtrl.value : null;
    if (ruleTypeId === null) return '';

    const rd = this.getRuleDataByType(ruleTypeId);
    if (!rd) return '';

    const selected = this.getSelectedCountForType(ruleTypeId);
    return selected + '/' + rd.maxNumOptions + (this.language == "it" ? " selezionati" : " selected");
  }

  // ================================
  // VALIDATION & SUBMIT (TUTTI I CONTROLLI QUI)
  // ================================
onSubmit(): void {
  console.log('Inizio validazione form...');
  
  // ✅ Reset errori precedenti
  this.fieldErrors = {};
  
  const rulesArray = this.form.get('rules') as FormArray;
//  const errors: string[] = [];
  let hasErrors = false;
  //KS7464 rimosso errore in caso di regole vuote
  // Controlla se ci sono regole
  /* if (!rulesArray || rulesArray.length === 0) {
 
       this.srvNotify.add({
      severity: 'error',
      summary: this.translate.instant('common.error'),
      detail:  (this.language == "it" ? 'Devi aggiungere almeno una regola' : 'You must add a rule almost')
    });
    
    hasErrors = true;
  } else {  */
    // Valida ogni regola
    rulesArray.controls.forEach((group: AbstractControl, index: number) => {
      if (group instanceof FormGroup) {
        // ✅ Inizializza errori per questo indice
        this.fieldErrors[index] = {};
        
        const ruleTypeIdControl = group.get('ruleTypeId');
        const objectTypeIdControl = group.get('objectTypeId');
        const inputValueControl = group.get('inputValue');

        const ruleTypeId = ruleTypeIdControl ? ruleTypeIdControl.value : null;
        const objectTypeId = objectTypeIdControl ? objectTypeIdControl.value : null;
        const inputValue = inputValueControl ? inputValueControl.value : null;
        
        // ✅ Controllo campi obbligatori
        if (!ruleTypeId) {
          let errorText =  (this.language == "it" ? 'Tipo regola è richiesto' : 'Rule Type is required');
          this.fieldErrors[index]['ruleTypeId'] = [errorText];
          hasErrors = true;
        }
        if (!objectTypeId) {
          let errorText = (this.language == "it" ? 'Tipo oggetto è richiesto' : 'Object Type is required');
          this.fieldErrors[index]['objectTypeId'] = [errorText];
          hasErrors = true;
        }
        
        // ✅ Controllo limiti maxNumOptions
        if (ruleTypeId) {
          const ruleData = this.getRuleDataByType(ruleTypeId);
          if (ruleData) {
            const selectedCount = this.getSelectedCountForType(ruleTypeId);
            if (selectedCount > ruleData.maxNumOptions) {
              if (!this.fieldErrors[index]['ruleTypeId']) {
                this.fieldErrors[index]['ruleTypeId'] = [];
              }
              let errorText = (this.language == "it" ? `Superato il limite massimo di ${ruleData.maxNumOptions}` : `Exceeded maximum limit of ${ruleData.maxNumOptions}`);

              this.fieldErrors[index]['ruleTypeId'].push(errorText);
              hasErrors = true;
            }
          }
        }
        
        // ✅ Controllo valore input se richiesto
        const ruleData = this.getRuleDataByIndexControl(index);
        if (ruleData && ruleData.uiType === 'EDIT') {
          if (!inputValue && inputValue !== 0) {
             let errorText = (this.language == "it" ? 'Valore input è richiesto' : 'Input Value is required');
            this.fieldErrors[index]['inputValue'] = [errorText];
            hasErrors = true;
          } else if (ruleData.fieldType === "Numeric" && inputValue) {
            const inputErrors: string[] = [];
            
            // ✅ Controllo se è un numero intero
            const numValue = parseFloat(inputValue);
            if (isNaN(numValue)) {
              let errorText = (this.language == "it" ? 'Deve essere un numero' : 'It must be a number');
              inputErrors.push(errorText);
            } else if (numValue !== Math.floor(numValue)) {
              let errorText = (this.language == "it" ? 'Deve essere un numero intero' : 'It must be an integer number');
              inputErrors.push(errorText);
            } else {
              // ✅ Controllo min/max
              const objectType = this.getObjectTypeByIndexControl(index);
              if (objectType) {
                if (objectType.valMin !== null && objectType.valMin !== undefined && numValue < objectType.valMin) {
                  let errorText = (this.language == "it" ? `Deve essere almeno ${objectType.valMin}` : `It must be ${objectType.valMin} almost`);
                  inputErrors.push(errorText);
                }
                if (objectType.valMax !== null && objectType.valMax !== undefined && numValue > objectType.valMax) {
                  let errorText = (this.language == "it" ? `Non può essere maggiore di ${objectType.valMax}` : `It can not upper than ${objectType.valMax}`);
                  inputErrors.push(errorText);
                }
              }
            }
            
            if (inputErrors.length > 0) {
              this.fieldErrors[index]['inputValue'] = inputErrors;
              hasErrors = true;
            }
          }
        }
      }
    });
//  }//questa graffa da commentare dopo KS7464
  
  // ✅ Se ci sono errori, mostra notifica unica
  if (hasErrors) {
    console.error('Errori di validazione trovati');
    /*
    this.srvNotify.add({
      severity: 'error',
      summary: this.translate.instant('common.error'),
      detail: 'Correggi i campi evidenziati in rosso prima di procedere'
    });
    */
    return;
  }
  
  // ✅ Form valido - procedi con la creazione della regola
  console.log('Form validato con successo!');
  this.buildRules();
}
// ✅ Helper per verificare se un campo ha errori
hasFieldError(index: number, field: string): boolean {
  return this.fieldErrors[index] && this.fieldErrors[index][field] && this.fieldErrors[index][field].length > 0;
}
  buildRules(){

  const validDetails: MessageInstanceRuleDetail[] = [];
  
  for (let i = 0; i < this.rules.value.length; i++) {
    const r = this.rules.value[i];
    if (r.ruleTypeId != null && r.objectTypeId != null) {
      const detail = new MessageInstanceRuleDetail();
      detail.objectId = r.ruleTypeId;
      detail.objectTypeId = r.objectTypeId;
      const objType = this.miObjectTypesList.find(x => x.id === r.objectTypeId);
      detail.objectTypeCode = objType ? objType.code : null;
      detail.objectValue = r.inputValue;
      validDetails.push(detail);
    }
  }

  if (validDetails.length === 0) {
    this.rulesCreated.emit([]);
    return;
  }

  const rule = new MessageInstanceRule();
  rule.messageInstanceId = this.miId;
  rule.ruleType = 'TR';
  rule.ruleTypeId = 666;
  rule.ruleScope = this.scope === 'emission' ? 'emission' : 'fruition';
  rule.messageInstanceRuleDetail = validDetails;
  rule.ochannelId = this.idTouchpoint;
  rule.touchpointTarget = this.targetScope;

  this.rulesCreated.emit([rule]);
}

// ✅ Helper per ottenere gli errori di un campo
getFieldErrors(index: number, field: string): string[] {
  return this.hasFieldError(index, field) ? this.fieldErrors[index][field] : [];
}

// ✅ Helper per ottenere la classe CSS di errore
getFieldErrorClass(index: number, field: string): string {
  return this.hasFieldError(index, field) ? 'field-error' : '';
}

}



// WEBPACK FOOTER //
// ./src/app/views/campaign-3/components/touchpoint-rule-popup/touchpoint-rule-popup.component.ts